import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import TwilioPhoneAddEditModal from "pages/AllCommonModal/TwilioPhoneAddEditModal/twilio-phone-add-edit-modal";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useTwilioPhoneContext } from "libs/context/TwilioPhoneContext";
import {
  submitTwilioPhoneFilterData,
  deleteTwilioPhone,
} from "model/TwilioPhoneModel";
import Loaders from "components/Common/Loaders";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const mapTwilioPhoneRows = (list = []) =>
  (Array.isArray(list) ? list : []).map((item = {}) => ({
    ...item,
    id: item?.id || item?._id || "",
    phoneId: item?.phoneId || item?.id || item?._id || "",
    ID: item?.id || item?._id,
  }));

const TwilioPhoneNumber = () => {
  const { fetchTwilioPhoneContextData, twilioPhoneContextData } =
    useTwilioPhoneContext() || {};
  const [twilioPhoneAddEditModalOpen, setTwilioPhoneAddEditModalOpen] =
    useState(false);
  const [modal1, setModal1] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [twilioPhoneData, setTwilioPhoneData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

  const toggleViewModal = () => setModal1(!modal1);

  const filteredData = useMemo(() => {
    if (!searchQuery?.trim()) return twilioPhoneData;
    return twilioPhoneData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [twilioPhoneData, searchQuery]);

  useEffect(() => {
    fetchTwilioPhoneContextData?.();
    getTwilioPhoneList();
  }, []);

  useEffect(() => {
    const valueReModified = twilioPhoneContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
      type: col?.type,
    }));
    setTableMetadata(valueReModified);
  }, [twilioPhoneContextData]);

  const getTwilioPhoneList = async () => {
    setLoading(true);
    const response = await submitTwilioPhoneFilterData({});
    const listData = response?.data;

    if (response) {
      setTwilioPhoneData(mapTwilioPhoneRows(listData));
    } else {
      setTwilioPhoneData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (data) => {
    setLoading(true);
    const response = await submitTwilioPhoneFilterData(data);
    const filterData = response?.data;

    if (response) {
      setTwilioPhoneData(mapTwilioPhoneRows(filterData));
    } else {
      setTwilioPhoneData([]);
    }
    setLoading(false);
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    setLoading(true);
    const payload = { search: value };
    const response = await submitTwilioPhoneFilterData(payload);
    const searchData = response?.data;

    if (response) {
      setTwilioPhoneData(mapTwilioPhoneRows(searchData));
    } else {
      setTwilioPhoneData([]);
    }
    setLoading(false);
  };

  const handleDelete = async (row = {}) => {
    const phoneId = String(row?.phoneId ?? row?.id ?? row?._id ?? "").trim();
    if (!phoneId) {
      toast.error("Phone ID is missing");
      return;
    }

    const swalResult = await Swal.fire({
      title: "Delete Twilio phone number?",
      text: "Are you sure you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    setLoading(true);
    try {
      const res = await deleteTwilioPhone(phoneId);
      if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
        toast.success(res?.message || "Deleted successfully");
        await getTwilioPhoneList();
        return;
      }
      toast.error(res?.message || "Unable to delete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-3">Twilio Phone Number</h5>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by phone or name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={true}
                  onDelete={handleDelete}
                  isDeleteAccess={true}
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Twilio Phone Number"}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <TwilioPhoneAddEditModal
            setTwilioPhoneAddEditModalOpen={setTwilioPhoneAddEditModalOpen}
            twilioPhoneAddEditModalOpen={twilioPhoneAddEditModalOpen}
            getTwilioPhoneList={getTwilioPhoneList}
            itemId={itemId}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Twilio Phone Number"
            handleSubmit={handleFilterSubmit}
            filterData={twilioPhoneContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

TwilioPhoneNumber.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(TwilioPhoneNumber);
